import { useEffect, useState } from "react";
import type {
  Metromap,
  PaymentMetromapList,
  Step,
  Approver,
} from "@/app/types/payments";
import { ICON_BASE_PATH } from "@/app/utils/config";
import { gxCurrencyFormat } from "@/app/utils/helpers/gxCurrencyFormat";
import { gxDateTimeFormat } from "@/app/utils/helpers/gxDateTimeFormat";
import { SvgIcon } from "@progress/kendo-react-common";
import { chevronDownIcon, chevronUpIcon } from "@progress/kendo-svg-icons";
import { AnimatePresence, motion } from "framer-motion";
import { useT } from "@/app/hooks/useT";

interface MetroMapProp {
  metroMap: Metromap;
}

const formatCurrencyStep = (
  amount: number,
  currency: string,
  suffix: string,
) => {
  const { symbol, integer, decimal } = gxCurrencyFormat(amount, currency);

  return { symbol: symbol, amount: `${integer}${decimal} ${suffix}` };
};

const getStepIcon = (item: PaymentMetromapList): string => {
  if (item?.id === 1 || item?.id === 3 || item?.id === 4 || item?.id === 5) {
    return `${ICON_BASE_PATH}success-tick.svg`;
  }

  if (item?.id === 2) {
    const stepId = (item?.step as Step).id;
    if (stepId === 1 || stepId === 5)
      return `${ICON_BASE_PATH}success-tick.svg`;
    if (stepId === 2) return `${ICON_BASE_PATH}awaiting-approval.svg`;
    if (stepId === 3) return `${ICON_BASE_PATH}reject-tick.svg`;
  }

  return `${ICON_BASE_PATH}s-f-r-tick.svg`;
};

const getApprovalIcon = (approvedStatus: string): string => {
  if (approvedStatus === "Pending" || approvedStatus === "PENDING") {
    return `${ICON_BASE_PATH}pending-icon.svg`;
  }
  if (approvedStatus === "Approved" || approvedStatus === "Self Approved") {
    return `${ICON_BASE_PATH}green_tick.svg`;
  }
  if (approvedStatus === "Rejected") {
    return `${ICON_BASE_PATH}reject-icon.svg`;
  }
  return `${ICON_BASE_PATH}s-f-r-icon.svg`;
};

export const MetroMap = ({ metroMap }: MetroMapProp) => {
  const t = useT();
  const [metroMapList, setMetroMapList] = useState<PaymentMetromapList[]>();
  const [isPaymentProgressOpen, setIsPaymentProgressOpen] =
    useState<boolean>(true);

  useEffect(() => {
    const tempArr: PaymentMetromapList[] = []; // ✅ Fix 4: `let` → `const`

    // ✅ Fix 5: `let` → `const` for never-reassigned variables
    const allApproved = metroMap?.approvalDetails?.approvers?.every(
      (item: Approver) => item?.approvedStatus.toUpperCase() === "APPROVED",
    );
    const isPending = metroMap?.approvalDetails?.approvers?.some(
      (item: Approver) => item?.approvedStatus.toUpperCase() === "PENDING",
    );
    const isRejected =
      metroMap?.approvalDetails?.approvers?.some(
        (item: Approver) => item?.approvedStatus.toUpperCase() === "REJECTED",
      ) ?? false; // ✅ Fix 6: `|| false` → `?? false`

    const isSelfApproved =
      metroMap?.approvalDetails?.approvers?.some(
        (item: Approver) => item?.approvedStatus === "Self Approved",
      ) ?? false; // ✅ Fix 7: `|| false` → `?? false`

    const getApproveStatus = () => {
      if (allApproved) return { status: t("approved"), id: 1 };
      if (isPending && !isRejected)
        return { status: t("approval_in_progress"), id: 2 };
      if (isRejected) return { status: t("rejected"), id: 3 };
      if (isSelfApproved) return { status: t("self_approved"), id: 5 };
      return { status: t("sent_for_repair"), id: 4 };
    };

    const approveStatus = getApproveStatus();

    if (metroMap?.initiatedDetails) {
      tempArr[0] = {
        id: 1,
        step: t("payment_initiated"),
        dateTime: metroMap?.initiatedDetails.initiatedAt,
        initiatedBy: metroMap?.initiatedDetails?.initiatedBy,
      };
    }

    if (metroMap?.approvalDetails) {
      const approvers = metroMap?.approvalDetails?.approvers.map(
        (item, index) => ({ ...item, approverIndex: index + 1 }),
      );
      const rejectReason = metroMap?.approvalDetails?.approvers.find(
        (item) => item?.reason,
      );
      tempArr[1] = {
        id: 2,
        step: approveStatus,
        approvalsCompleted: metroMap?.approvalDetails?.approvalsCompleted,
        approvalsRequired: metroMap?.approvalDetails?.approvalsRequired,
        approvers: approvers,
        ...(rejectReason?.reason && { rejectReason: rejectReason?.reason }),
      };
    }

    if (metroMap?.debitDetails) {
      tempArr[2] = {
        id: 3,
        step: formatCurrencyStep(
          metroMap?.debitDetails?.amount,
          metroMap?.debitDetails?.debitCurrency,
          "was debited",
        ),
        dateTime: metroMap?.debitDetails?.debitedAt ?? "",
      };
    }

    if (metroMap?.creditDetails) {
      tempArr[3] = {
        id: 4,
        step: formatCurrencyStep(
          metroMap?.creditDetails?.amount,
          metroMap?.creditDetails?.creditCurrency,
          `sent to ${metroMap?.creditDetails?.creditorName}`,
        ),
        dateTime: metroMap?.creditDetails?.creditedAt ?? "",
      };
    }

    if (metroMap?.completionDetails) {
      tempArr[4] = {
        id: 5,
        step: "Payment Completed",
        dateTime: metroMap?.completionDetails?.completedAt ?? "",
      };
    }

    setMetroMapList(tempArr);
  }, [metroMap]);

  return (
    <div className="gx-transaction-detail-card gx-transaction-metromap-progress">
      <button
        type="button"
        className="gx-transaction-detail-card-header"
        onClick={() => setIsPaymentProgressOpen(!isPaymentProgressOpen)}
      >
        <h2 className="gx-transaction-detail-card-title gx-h3-b">
          {t("payment_timeline")}
        </h2>
        <SvgIcon
          icon={isPaymentProgressOpen ? chevronUpIcon : chevronDownIcon}
          size="small"
        />
      </button>

      {isPaymentProgressOpen && (
        <div className="gx-transaction-metromap-progress-timeline">
          {metroMapList?.map((item: PaymentMetromapList, index: number) => {
            const step = item?.step;

            return (
              <div
                key={item?.id}
                className="gx-transaction-metromap-progress-step"
              >
                <div className="gx-transaction-metromap-progress-dashedline-container">
                  <img
                    src={getStepIcon(item)} // ✅ Fix 2 applied
                    alt="step-icon"
                  />
                  {metroMapList.length - 1 > index && (
                    <span className="gx-transaction-metromap-progress-dashedline" />
                  )}
                </div>

                {/* --- STEP CONTENT --- */}
                <div className="gx-transaction-metromap-progress-content">
                  <div className="gx-transaction-metromap-progress-title-container">
                    {typeof step === "object" && "symbol" in step && (
                      <span className="gx-p12-m gx-transaction-metromap-progress-title">
                        {step?.symbol} {step?.amount}
                      </span>
                    )}
                    {typeof step === "object" && "status" in step && (
                      <span className="gx-p12-m gx-transaction-metromap-progress-title">
                        {step?.status}
                      </span>
                    )}
                    {typeof step !== "object" && (
                      <span className="gx-p12-m gx-transaction-metromap-progress-title">
                        {step}
                      </span>
                    )}

                    {item?.dateTime && (
                      <div className="gx-p12-r gx-transaction-metromap-progress-date">
                        {gxDateTimeFormat(item?.dateTime)}
                      </div>
                    )}
                  </div>

                  {(item?.initiatedBy || item?.approvers) && (
                    <AnimatePresence>
                      {item?.rejectReason && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="gx-transaction-metromap-progress-approval-step"
                        >
                          <span className="gx-p12-m">{item?.rejectReason}</span>
                        </motion.div>
                      )}
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="gx-transaction-metromap-progress-approval-step"
                      >
                        {item?.initiatedBy && (
                          <div className="gx-p12-m gx-transaction-metromap-progress-info-box">
                            <span className="gx-p12 gx-transaction-metromap-progress-info-label">
                              {t("initiated_by")}:{" "}
                            </span>
                            <span className="gx-p12-m gx-mt-2">
                              {item?.initiatedBy}
                            </span>
                          </div>
                        )}

                        <div className="gx-p12-m gx-transaction-metromap-progress-approval-list">
                          {item?.approvalsRequired && (
                            <span className="gx-p12-m gx-transaction-metromap-progress-approval-count-status">
                              {item?.approvalsCompleted} {t("of_text")}{" "}
                              {item?.approvalsRequired}{" "}
                              {t("approvals_completed")}
                            </span>
                          )}

                          <ul className="gx-transaction-metromap-progress-approval-list-status">
                            {item?.approvers?.map((approval) => (
                              <li
                                key={approval?.approvedAt}
                                className="gx-p12-m"
                              >
                                <img
                                  src={getApprovalIcon(
                                    approval?.approvedStatus,
                                  )} // ✅ Fix 3 applied
                                  alt={approval?.approvedStatus}
                                />
                                {(approval?.approvedName ||
                                  approval?.approvedStatus === "Pending" ||
                                  approval?.approvedStatus === "PENDING") && (
                                  <span className="gx-p12-m">
                                    {approval?.approvedName ?? "Pending"}
                                  </span>
                                )}
                                {approval?.approvedAt && (
                                  <span className="gx-p12-r gx-transaction-metromap-progress-approved-date">
                                    {gxDateTimeFormat(approval?.approvedAt)}
                                  </span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
